import * as yup from 'yup'
import {Option} from 'jbc-front/components/presenters/form/Select'

const DEFAULT_MIN_VALUE = 0
const DEFAULT_MAX_VALUE = 999
const BIRTHDAY_MAX_DAYS = 364
const BIRTHDAY_MAX_MONTHS = 11

const dateUnitCountLabel = (dateUnitValue: string): string => {
  switch (dateUnitValue) {
    case 'day':
      return '日数'
    case 'month':
      return '月数'
    case 'year':
      return '年数'
    default:
      return '日数'
  }
}

const containsNonDigit = (value: string): boolean => {
  return /\D/.test(value)
}

const countHtmlTextLength = (htmlStr: string) => {
  const htmlStrWithNewline = htmlStr.replace(/<br\s*\/?>/gi, '\n')
  const parser = new DOMParser()
  const html = parser.parseFromString(htmlStrWithNewline, 'text/html')
  const text = html.body.innerText
  return text.length
}

const testHtmlMaxLength =
  (maxLength: number) =>
  (value: string): boolean => {
    if (!value) return true
    return countHtmlTextLength(value) <= maxLength
  }

const optionSchema = yup.object().shape({
  value: yup.string().required(),
  label: yup.string().required(),
})

export const schema = yup.object().shape({
  color: yup.number().required(),
  name: yup
    .string()
    .trim()
    .required('アラート名を入力してください')
    .max(20, 'アラート名は20文字以下で入力してください'),
  description: yup.string().defined().max(255, '説明は255文字以下で入力してください'),
  baseDateType: optionSchema.required(),
  days: yup.string().test('days-test', '日数に適切な数値を入力してください', function (value) {
    const baseDateType = this.parent.baseDateType.value
    const dateUnit = this.parent.dateUnit
    const countLabel = dateUnitCountLabel(dateUnit)

    if (value === undefined || value === null || value === '') {
      return this.createError({message: `${countLabel}を入力してください`})
    }

    let maxValue = DEFAULT_MAX_VALUE
    if (baseDateType === 'birthday' && dateUnit === 'day') {
      maxValue = BIRTHDAY_MAX_DAYS
    } else if (baseDateType === 'birthday' && dateUnit === 'month') {
      maxValue = BIRTHDAY_MAX_MONTHS
    }

    if (containsNonDigit(value)) {
      return this.createError({
        message: `${countLabel}は${DEFAULT_MIN_VALUE}以上${maxValue}以下の整数を入力してください`,
      })
    }

    const valueNumber = Number(value)
    if (valueNumber < DEFAULT_MIN_VALUE || valueNumber > maxValue) {
      return this.createError({
        message: `${countLabel}は${DEFAULT_MIN_VALUE}以上${maxValue}以下の整数を入力してください`,
      })
    }

    return true
  }),
  offset: yup.string().test('offset-test', '年齢または勤続年数を入力してください', function (value) {
    const baseDateType = this.parent.baseDateType.value
    if (baseDateType !== 'age' && baseDateType !== 'service_years') {
      return true
    }
    const baseDateTypeLabel = this.parent.baseDateType.label

    if (value === undefined || value === null || value === '') {
      return this.createError({message: `${baseDateTypeLabel}を入力してください`})
    }

    if (containsNonDigit(value)) {
      return this.createError({
        message: `${baseDateTypeLabel}は${DEFAULT_MIN_VALUE}以上${DEFAULT_MAX_VALUE}以下の整数を入力してください`,
      })
    }

    const valueNumber = Number(value)
    if (valueNumber < DEFAULT_MIN_VALUE || valueNumber > DEFAULT_MAX_VALUE) {
      return this.createError({
        message: `${baseDateTypeLabel}は${DEFAULT_MIN_VALUE}以上${DEFAULT_MAX_VALUE}以下の整数を入力してください`,
      })
    }

    return true
  }),
  alertTargets: yup
    .array(optionSchema.required())
    .required()
    .when('baseDateType', {
      is: (value: Option) => value?.value === 'age' || value?.value === 'birthday',
      then: (schema) => schema.min(1, '基準日で「誕生日」または「年齢」選択時は、対象者を一つ以上選択してください'),
    }),
  dateUnit: yup
    .string()
    .required()
    .when('baseDateType', {
      is: (value: Option) => value.value === 'birthday',
      then: (schema) => schema.notOneOf(['year'], '年は選択できません'),
    }),
  isBefore: yup.string().oneOf(['Before', 'After']).required(),
  reasonType: optionSchema.optional(),
  active: yup.boolean().required(),
  adminMailActive: yup.boolean().required(),
  adminMailSubject: yup.string().when('adminMailActive', {
    is: true,
    then: (schema) => schema.trim().required('件名を入力してください').max(255, '件名は255文字以下で入力してください'),
  }),
  adminMailBody: yup.string().when('adminMailActive', {
    is: true,
    then: (schema) =>
      schema
        .required('本文を入力してください')
        .test('maxLength', '本文は1000文字以下で入力してください', testHtmlMaxLength(1000)),
  }),
  employeeMailActive: yup.boolean().required(),
  employeeMailSubject: yup.string().when('employeeMailActive', {
    is: true,
    then: (schema) => schema.trim().required('件名を入力してください').max(255, '件名は255文字以下で入力してください'),
  }),
  employeeMailBody: yup.string().when('employeeMailActive', {
    is: true,
    then: (schema) =>
      schema
        .required('本文を入力してください')
        .test('maxLength', '本文は1000文字以下で入力してください', testHtmlMaxLength(1000)),
  }),
  selectedOfficeIds: yup.array(yup.string().required()).when('selectedAllOffices', {
    is: false,
    then: (schema) => schema.min(1, '適用事業所を設定してください'),
  }),
  selectedAllOffices: yup.boolean().required(),
})

export type Schema = yup.InferType<typeof schema>
